import { useCallback } from 'react';

import { useMutation } from '@apollo/react-hooks';

import { CateringDeclineReasons } from 'generated/rbi-graphql';
import { useStoreOrderStatus } from 'hooks/order-status';
import {
  ConfirmCateringOrder,
  GetStoreOrderQuery,
  IConfirmCateringMutationResponse,
  IConfirmCateringOrderInput,
} from 'remote/queries/order';
import { CateringStatus } from 'state/order/types';

import { SelectionValue } from './catering-confirmation-modal/decline-modal-body';
import { IUpdateOrderStatusInput, IUseStoreOrderParams } from './types';

enum Decision {
  ACCEPT = 'Accept',
  DECLINE = 'Decline',
}
export const useStoreOrder = ({ storeOrderJwt }: IUseStoreOrderParams) => {
  const queryResult = useStoreOrderStatus({ storeOrderJwt });
  const [fireMutation, { data: mutationResult, loading: mutationLoading }] = useMutation<
    IConfirmCateringMutationResponse,
    IConfirmCateringOrderInput
  >(ConfirmCateringOrder);
  const { serverOrder, loading: queryResultLoading } = queryResult;
  const updateOrderStatus = useCallback(
    (options: IUpdateOrderStatusInput, reason?: SelectionValue) => {
      const decision = {
        [CateringStatus.STORE_APPROVED]: Decision.ACCEPT,
        [CateringStatus.STORE_DECLINED]: Decision.DECLINE,
      }[options.status];
      if (!decision) {
        return;
      }
      const isDeclined = decision === Decision.DECLINE;
      const variables: IConfirmCateringOrderInput = {
        input: {
          cateringJwt: storeOrderJwt,
          decision,
        },
      };
      if (isDeclined) {
        variables.input.declineReason = (reason && reason.value) as CateringDeclineReasons;
      }

      fireMutation({
        variables,
        refetchQueries: [{ query: GetStoreOrderQuery, variables: { storeOrderJwt } }],
      });
    },
    [fireMutation, storeOrderJwt]
  );

  return {
    updateOrderStatus,
    loading: queryResultLoading || mutationLoading,
    serverOrder,
    mutationResult,
  };
};
